import { lazy, Suspense, useEffect, useState } from "react";
import { Reveal, Eyebrow, Btn } from "./primitives.jsx";
import { useSiteContent } from "../content.js";

// The checkout modal pulls in Stripe's React bindings — heavy, and only needed
// once the visitor actually clicks "buy". Lazy-load it so the homepage bundle
// (and Stripe's JS) stays out of the critical path; mount it only when opened.
const Checkout = lazy(() => import("./checkout.jsx"));

export default function Book({ onOpenStory, onOpenForeword }) {
  const { book: copy } = useSiteContent();
  const [tab, setTab] = useState("direct");
  const AMAZON_URL = "https://www.amazon.com/Day-Yankees-Made-Me-Shave-ebook/dp/B08FV8TLGW";
  const BOOK_COVER =
    "https://m.media-amazon.com/images/S/compressed.photo.goodreads.com/books/1554949076i/45018799.jpg";
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  // Purchase deep link: arriving with ?buy=1 (usually via the /buy link,
  // which App.jsx rewrites to this) opens the checkout modal immediately — the
  // link Greg hands out when someone asks where to buy the book. The param is
  // stripped afterwards so a refresh, or sharing the URL from the address bar,
  // lands on the normal homepage instead of re-opening checkout.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get("buy")) return;
    setTab("direct");
    setCheckoutOpen(true);
    params.delete("buy");
    const qs = params.toString();
    window.history.replaceState(
      null,
      "",
      window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash,
    );
  }, []);

  return (
    <>
      <section id="book" data-screen-label="01 The Book" className="section book">
        <div className="section-inner">
          <Reveal>
            <Eyebrow index="01">{copy.eyebrow}</Eyebrow>
          </Reveal>

          <div className="book-grid">
            <div className="book-cover-col">
              <Reveal delay={120}>
                <div className="book-cover">
                  <div className="book-cover-spine" />
                  <img
                    className="book-cover-img"
                    src={BOOK_COVER}
                    alt="The Day The Yankees Made Me Shave — book cover"
                    loading="lazy"
                  />
                </div>
                <div className="book-cover-shadow" />
              </Reveal>
              <Reveal delay={400}>
                <div className="book-stats">
                  <div>
                    <span className="mono">312</span> Pages
                  </div>
                  <div>
                    <span className="mono">14</span> Chapters
                  </div>
                  <div>
                    <span className="mono">★ 4.7</span> · 184 Reviews
                  </div>
                </div>
              </Reveal>
              {/* Quick-buy CTA shown only when the grid is single-column (≤980px):
                  on mobile the real purchase button sits far below the cover,
                  past the title, lede, pull quote and two callouts. This puts a
                  buy action right under the cover so visitors don't have to hunt
                  for it. Hidden on desktop, where the right-column CTA is already
                  visible beside the sticky cover. */}
              <Reveal delay={480}>
                <div className="book-quickbuy">
                  <Btn
                    primary
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setTab("direct");
                      setCheckoutOpen(true);
                    }}
                  >
                    {copy.quickBuyCta}
                  </Btn>
                </div>
              </Reveal>
            </div>

            <div className="book-text-col">
              <Reveal delay={200}>
                <h2 className="display h-lg book-title">
                  {copy.titleBefore} <em>{copy.titleEmphasis}</em>.
                </h2>
              </Reveal>
              <Reveal delay={300}>
                <p className="lede" style={{ marginTop: 24 }}>
                  {copy.lede}
                </p>
              </Reveal>

              <Reveal delay={400}>
                <div className="book-pull">
                  <div className="book-pull-mark">"</div>
                  <p>{copy.pullQuote}</p>
                  <span className="book-pull-attr mono">- {copy.pullAttribution}</span>
                </div>
              </Reveal>

              <Reveal delay={430}>
                <a
                  className="book-foreword-callout"
                  href="/foreword-by-tony-la-russa"
                  onClick={(e) => {
                    // Plain click opens the in-app overlay (no reload). Modifier/
                    // middle clicks fall through to the href so "open in new tab"
                    // lands on the full standalone foreword page.
                    if (
                      onOpenForeword &&
                      e.button === 0 &&
                      !e.metaKey &&
                      !e.ctrlKey &&
                      !e.shiftKey
                    ) {
                      e.preventDefault();
                      onOpenForeword();
                    }
                  }}
                >
                  <span className="book-foreword-monogram mono" aria-hidden>
                    TLR
                  </span>
                  <span className="book-foreword-main">
                    <span className="book-foreword-kicker mono">
                      Foreword · Hall of Fame manager
                    </span>
                    <span className="book-foreword-title">Tony La Russa wrote the foreword.</span>
                    <span className="book-foreword-quote">
                      "…the ones chosen for his first book make for a Major League read."
                    </span>
                    <span className="book-foreword-go mono">Read it →</span>
                  </span>
                </a>
              </Reveal>

              <Reveal delay={450}>
                <a
                  className="book-shave-callout"
                  href="/the-day-the-yankees-made-me-shave"
                  onClick={(e) => {
                    // Plain click opens the in-app overlay (no reload). Modifier/
                    // middle clicks fall through to the href so "open in new tab"
                    // still lands on the full standalone page.
                    if (onOpenStory && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
                      e.preventDefault();
                      onOpenStory();
                    }
                  }}
                >
                  <span className="book-shave-kicker mono">The story behind the title</span>
                  <span className="book-shave-text">
                    The Yankees finally dropped the no-beard rule in 2025. I'm the guy whose book it
                    named.
                  </span>
                  <span className="book-shave-go mono">Read it →</span>
                </a>
              </Reveal>

              <Reveal delay={500}>
                <div className="book-tabs">
                  {[
                    {
                      k: "direct",
                      l: copy.directTabLabel,
                      sub: copy.directTabSub,
                      badge: copy.directBadge,
                    },
                    { k: "amazon", l: copy.amazonTabLabel, sub: copy.amazonTabSub },
                  ].map((t) => (
                    <button
                      key={t.k}
                      className={`book-tab book-tab-${t.k} ${tab === t.k ? "is-active" : ""}`}
                      onClick={() => setTab(t.k)}
                    >
                      {t.badge && <span className="book-tab-badge mono">{t.badge}</span>}
                      <span className="book-tab-l">{t.l}</span>
                      <span className="book-tab-sub mono">{t.sub}</span>
                    </button>
                  ))}
                </div>
                {tab === "direct" && (
                  <>
                    {copy.directNote && (
                      <p className="book-tab-note book-tab-note-direct">{copy.directNote}</p>
                    )}
                    <div className="book-cta">
                      <Btn
                        primary
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setCheckoutOpen(true);
                        }}
                      >
                        {copy.directCta}
                      </Btn>
                    </div>
                  </>
                )}
                {tab === "amazon" && (
                  <>
                    {copy.amazonNote && (
                      <p className="book-tab-note book-tab-note-amazon">{copy.amazonNote}</p>
                    )}
                    <div className="book-cta">
                      <Btn
                        primary
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setTab("direct");
                          setCheckoutOpen(true);
                        }}
                      >
                        {copy.directCta}
                      </Btn>
                      <Btn href={AMAZON_URL} target="_blank" rel="noopener">
                        {copy.amazonCta}
                      </Btn>
                    </div>
                  </>
                )}
              </Reveal>

              <Reveal delay={650}>
                <div className="book-quotes">
                  {copy.quotes.slice(0, 2).map((q, i) => (
                    <div className="book-quote" key={i}>
                      <p className="book-quote-text">"{q.text}"</p>
                      <div className="book-quote-attr">
                        <span className="book-quote-who">{q.who}</span>
                        <span className="book-quote-role mono">{q.role}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>

              <Reveal delay={800}>
                <div className="book-teaser">
                  <div className="book-teaser-bar">
                    <span className="mono">{copy.teaserTag}</span>
                    <span className="book-teaser-dot" />
                    <span className="mono">{copy.teaserDate}</span>
                  </div>
                  <p>{copy.teaserText}</p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
      {checkoutOpen && (
        <Suspense fallback={null}>
          <Checkout
            open={checkoutOpen}
            initialEdition={tab === "amazon" ? "standard" : "signed"}
            onClose={() => setCheckoutOpen(false)}
          />
        </Suspense>
      )}
    </>
  );
}

const bookStyles = document.createElement("style");
bookStyles.textContent = `
.book { background: var(--navy-900); }
.book-grid {
  display: grid;
  grid-template-columns: minmax(300px, 440px) 1fr;
  gap: 80px;
  margin-top: 56px;
  align-items: start;
}
@media (max-width: 980px) { .book-grid { grid-template-columns: 1fr; gap: 56px; } }

.book-cover-col { position: relative; position: sticky; top: 120px; }
@media (max-width: 980px) {
  .book-cover-col { position: static; max-width: 280px; margin: 0 auto; width: 100%; }
  .book-cover { transform: none; }
}
.book-cover {
  position: relative;
  aspect-ratio: 2/3;
  background: #1a0606;
  border: 1px solid rgba(189, 155, 96, 0.3);
  box-shadow:
    0 60px 80px -30px rgba(0, 0, 0, 0.7),
    0 30px 40px -20px rgba(0, 0, 0, 0.5);
  transform: perspective(1500px) rotateY(-8deg) rotateX(2deg);
  transition: transform 600ms cubic-bezier(.2,.8,.2,1);
  overflow: hidden;
}
.book-cover-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.book-cover:hover { transform: perspective(1500px) rotateY(-3deg) rotateX(0deg) translateY(-6px); }
.book-cover-spine {
  position: absolute; left: -1px; top: 0; bottom: 0; width: 8px;
  background: linear-gradient(90deg, #1a0606, #2d0e0e);
  border-right: 1px solid rgba(189, 155, 96, 0.3);
}

.book-stats { display: flex; gap: 24px; margin-top: 32px; font-family: var(--sans); font-size: 0.75rem; color: var(--bone-dim); flex-wrap: wrap; }
@media (max-width: 540px) {
  .book-stats { gap: 14px; flex-wrap: nowrap; align-items: center; }
  .book-stats > div { font-size: 0.75rem; white-space: nowrap; }
  .book-stats .mono { font-size: 0.8125rem; }
}
.book-stats .mono { color: var(--gold); font-size: 0.875rem; font-weight: 600; }

/* Quick-buy button under the cover — only when the grid is single-column.
   On desktop the right-column purchase CTA sits beside the sticky cover, so
   this would be redundant; hide it there. */
.book-quickbuy { display: none; }
@media (max-width: 980px) {
  .book-quickbuy { display: flex; margin-top: 24px; }
  .book-quickbuy .btn { width: 100%; justify-content: center; }
}

.book-title { color: var(--bone); }
.book-title em { font-style: italic; color: var(--royal-blue-glow); font-variation-settings: "opsz" 144; }

.book-pull {
  margin-top: 40px;
  padding: 28px 32px;
  border-left: 2px solid var(--royal-blue-glow);
  background: rgba(0, 100, 194, 0.06);
  position: relative;
}
.book-pull-mark { position: absolute; top: -16px; left: 16px; font-family: var(--serif); font-size: 5rem; color: var(--gold); line-height: 1; }
.book-pull p { font-family: var(--serif); font-size: clamp(1.25rem, 2vw, 1.625rem); font-style: italic; color: var(--bone); margin: 0 0 16px 0; line-height: 1.3; }
.book-pull-attr { font-size: 0.6875rem; letter-spacing: 0.16em; text-transform: uppercase; color: var(--gold); }

.book-tabs { display: grid; grid-template-columns: 1.4fr 1fr; margin-top: 48px; border-top: 1px solid var(--rule); }
.book-tab {
  background: transparent; border: none;
  border-bottom: 1px solid var(--rule);
  padding: 18px 16px; text-align: left;
  cursor: pointer;
  display: flex; flex-direction: column; gap: 4px;
  position: relative;
  color: var(--bone-dim);
  transition: color 200ms, background 200ms;
}
.book-tab:hover { color: var(--bone); }
.book-tab.is-active { color: var(--royal-blue-glow); }
.book-tab.is-active::after { content: ""; position: absolute; left: 0; bottom: -1px; right: 0; height: 1px; background: var(--royal-blue-glow); }
.book-tab-l { font-size: 0.875rem; font-weight: 500; }
.book-tab-sub { font-size: 0.6875rem; opacity: 0.7; white-space: nowrap; }

.book-tab-direct {
  background: linear-gradient(180deg, rgba(189, 155, 96, 0.08), rgba(189, 155, 96, 0.02));
  border-top: 1px solid rgba(189, 155, 96, 0.35);
  margin-top: -1px;
}
.book-tab-direct:hover { background: linear-gradient(180deg, rgba(189, 155, 96, 0.14), rgba(189, 155, 96, 0.04)); }
.book-tab-direct.is-active { color: var(--gold); }
.book-tab-direct.is-active::after { background: var(--gold); }
.book-tab-direct .book-tab-sub { color: var(--gold); opacity: 0.85; }

.book-tab-badge {
  position: absolute;
  top: -9px; left: 14px;
  background: var(--gold);
  color: var(--navy-900);
  font-size: 0.5625rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 2px;
  line-height: 1;
}

.book-tab-note {
  font-family: var(--serif);
  font-size: 0.875rem;
  line-height: 1.55;
  color: var(--bone-dim);
  margin: 18px 0 0 0;
  padding: 14px 16px;
  border-left: 2px solid var(--rule);
}
.book-tab-note-direct {
  border-left-color: var(--gold);
  background: rgba(189, 155, 96, 0.04);
  color: var(--bone);
}
.book-tab-note-amazon {
  border-left-color: rgba(189, 155, 96, 0.5);
  background: rgba(0, 0, 0, 0.18);
}

.book-cta { display: flex; gap: 14px; margin-top: 20px; flex-wrap: wrap; align-items: center; }

.book-quotes { margin-top: 56px; display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
@media (max-width: 768px) { .book-quotes { grid-template-columns: 1fr; } }
.book-quote { padding: 24px; border: 1px solid var(--rule); background: rgba(189, 155, 96, 0.02); }
.book-quote-text { font-family: var(--serif); font-style: italic; font-size: 1rem; line-height: 1.4; color: var(--bone); margin: 0 0 16px 0; }
.book-quote-attr { display: flex; flex-direction: column; gap: 2px; }
.book-quote-who { font-weight: 600; font-size: 0.8125rem; color: var(--bone); }
.book-quote-role { font-size: 0.625rem; letter-spacing: 0.16em; text-transform: uppercase; color: var(--gold); opacity: 0.8; }

.book-teaser {
  margin-top: 56px;
  padding: 28px 32px;
  border: 1px dashed var(--royal-blue-bright);
  background: linear-gradient(135deg, rgba(0, 100, 194, 0.1), rgba(0, 70, 135, 0.04));
}
.book-teaser-bar { display: flex; align-items: center; gap: 14px; font-size: 0.6875rem; letter-spacing: 0.18em; text-transform: uppercase; color: var(--royal-blue-glow); margin-bottom: 12px; }
.book-teaser-dot { width: 4px; height: 4px; background: var(--royal-blue-glow); border-radius: 50%; }
.book-teaser p { margin: 0; font-family: var(--serif); font-size: 1.125rem; color: var(--bone); }
.book-teaser-link {
  display: inline-flex;
  margin-top: 18px;
  color: var(--gold);
  font-size: 0.8125rem;
  font-weight: 600;
}
.book-teaser-link:hover { color: var(--gold-bright); }

.book-shave-callout {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px 14px;
  margin: 28px 0 4px;
  padding: 16px 20px;
  text-decoration: none;
  border: 1px solid var(--rule-strong);
  border-left: 3px solid var(--gold);
  border-radius: 8px;
  background: rgba(189, 155, 96, 0.05);
  transition: background 200ms, border-color 200ms;
}
.book-shave-callout:hover {
  background: rgba(189, 155, 96, 0.1);
  border-left-color: var(--gold-bright);
}
.book-shave-kicker {
  width: 100%;
  color: var(--gold);
  font-size: 0.625rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
}
.book-shave-text {
  flex: 1 1 auto;
  font-family: var(--serif);
  font-size: 1rem;
  line-height: 1.5;
  color: var(--bone-dim);
}
.book-shave-go {
  color: var(--gold-bright);
  font-size: 0.75rem;
  letter-spacing: 0.1em;
  white-space: nowrap;
}
.book-shave-callout:hover .book-shave-go { color: var(--bone); }

/* Featured foreword callout — a Hall of Famer's endorsement gets top billing
   over the gold "story behind the title" callout directly beneath it, so it
   carries the richer, royal-blue treatment and a monogram badge. */
.book-foreword-callout {
  display: flex;
  align-items: flex-start;
  gap: 18px;
  margin: 40px 0 4px;
  padding: 20px 22px;
  text-decoration: none;
  border: 1px solid var(--royal-blue-bright);
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(0, 100, 194, 0.13), rgba(0, 70, 135, 0.04));
  box-shadow: 0 18px 44px -28px rgba(0, 0, 0, 0.85);
  transition: border-color 200ms, background 200ms, transform 200ms;
}
.book-foreword-callout:hover {
  border-color: var(--gold);
  background: linear-gradient(135deg, rgba(0, 100, 194, 0.2), rgba(0, 70, 135, 0.06));
  transform: translateY(-2px);
}
.book-foreword-monogram {
  flex: none;
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: var(--royal-blue);
  border: 1px solid var(--gold);
  color: var(--bone);
  font-size: 0.8125rem;
  letter-spacing: 0.06em;
  font-weight: 600;
}
.book-foreword-main {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
}
.book-foreword-kicker {
  color: var(--gold);
  font-size: 0.625rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}
.book-foreword-title {
  font-family: var(--serif);
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.25;
  color: var(--bone);
}
.book-foreword-quote {
  font-family: var(--serif);
  font-style: italic;
  font-size: 0.9375rem;
  line-height: 1.45;
  color: var(--bone-dim);
}
.book-foreword-go {
  margin-top: 8px;
  color: var(--gold-bright);
  font-size: 0.75rem;
  letter-spacing: 0.1em;
  white-space: nowrap;
}
.book-foreword-callout:hover .book-foreword-go { color: var(--bone); }
`;
document.head.appendChild(bookStyles);
